/** Maps GET thread-history messages into the SAME ChatBox part vocabulary a live turn produces, so restored history renders identically. */

import type { ChatMessage, ChatMessagePart, ChatRole } from "@mui/x-chat-headless"
import { type ArtifactEnvelope, isArtifactEnvelope } from "./artifact-contract"
import { narrowAttachment } from "./attachments-transport"
import type { Attachment } from "./attachments-transport"
import { narrowSkillProposals, type SkillProposal } from "./skill-proposal-contract"

interface RawHistoryMessage {
  id: string
  role: ChatRole
  text: string
  createdAt?: string
  artifacts: ArtifactEnvelope[]
  attachments: Attachment[]
}

export function attachmentParts(attachments: readonly Attachment[]): ChatMessagePart[] {
  return attachments.map((attachment) => ({ type: "data-attachment", id: attachment.id, data: attachment }))
}

/** Only the two conversational roles this surface renders; anything else is treated as assistant. */
function toChatRole(role: unknown): ChatRole {
  return role === "user" ? "user" : "assistant"
}

function narrowHistoryMessage(value: unknown): RawHistoryMessage | null {
  if (typeof value !== "object" || value === null) return null
  const candidate = value as { id?: unknown, role?: unknown, text?: unknown, createdAt?: unknown, artifacts?: unknown, attachments?: unknown }
  if (typeof candidate.id !== "string") return null
  return {
    id: candidate.id,
    role: toChatRole(candidate.role),
    text: typeof candidate.text === "string" ? candidate.text : "",
    createdAt: typeof candidate.createdAt === "string" ? candidate.createdAt : undefined,
    artifacts: Array.isArray(candidate.artifacts) ? candidate.artifacts.filter(isArtifactEnvelope) : [],
    attachments: Array.isArray(candidate.attachments)
      ? candidate.attachments.map(narrowAttachment).filter((record): record is Attachment => record !== null)
      : [],
  }
}

/** Build the ChatBox parts for one history message: one text part, then one part per artifact. */
function toMessageParts(message: RawHistoryMessage): ChatMessagePart[] {
  // Leading text part is unconditional: a live turn always emits an (even empty) text part before
  // artifacts, so an artifact-only row must carry the same empty text part or history/live diverge.
  const parts: ChatMessagePart[] = [{ type: "text", text: message.text }]
  parts.push(...attachmentParts(message.attachments))
  for (const artifact of message.artifacts) {
    parts.push({ type: "data-artifact", id: artifact.id, data: artifact })
  }
  return parts
}

/** Map one raw history message to a `ChatMessage`, or `null` if it fails the boundary guard. */
export function historyMessageToChatMessage(value: unknown): ChatMessage | null {
  const message = narrowHistoryMessage(value)
  if (message === null) return null
  return {
    id: message.id,
    role: message.role,
    createdAt: message.createdAt,
    parts: toMessageParts(message),
  }
}

/** Map an array of raw history messages, dropping any that fail the boundary guard. */
export function historyMessagesToChatMessages(messages: readonly unknown[]): ChatMessage[] {
  return messages.map(historyMessageToChatMessage).filter((message): message is ChatMessage => message !== null)
}

export function historySkillProposalsByMessageId(
  messages: readonly unknown[],
  sourceThreadId: string | null,
): Record<string, SkillProposal[]> {
  const byMessageId: Record<string, SkillProposal[]> = {}
  for (const value of messages) {
    if (typeof value !== "object" || value === null) continue
    const candidate = value as { id?: unknown, validated_skills?: unknown }
    if (typeof candidate.id !== "string") continue
    const proposals = narrowSkillProposals(candidate.validated_skills, sourceThreadId)
    if (proposals.length > 0) byMessageId[candidate.id] = proposals
  }
  return byMessageId
}

/** The last assistant message's id in render order — the `lastAssistantMsgId` continue guard. */
export function findLastAssistantMessageId(messages: readonly ChatMessage[]): string | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "assistant") return messages[index].id
  }
  return null
}
